// ═══════════════════════════════════════════════════════════════════════════
//  ExamsCmsTab.jsx — CRM DEL EXAMEN DE NIVEL · Fase 1: el banco de reactivos
//
//  Administra el banco `AssessmentItem` que ya vivía en la base de datos sin
//  panel que lo tocara. Crear, editar, borrar y ALIMENTAR con IA los reactivos,
//  con vista previa usando el MISMO renderer que ve el alumno (ItemRenderer).
//
//  Backend: /api/admin/assessment/*  (assessment_admin_routes.py)
//  No toca el examen de colocación certificado; solo su banco.
// ═══════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useMemo, useState } from 'react';
import ItemRenderer from '../../../../assessment/ItemRenderer';
import ExamsManager from './ExamsManager';
import Observabilidad from './Observabilidad';
import './ExamsCmsTab.css';

function cab() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  };
}

const API = '/api/admin/assessment';

// Familias de formato → cómo se edita cada una.
const FAMILIA = {
  choice: 'opcion', reading_choice: 'opcion',
  listen_choice: 'opcion', listen_tone: 'opcion',
  order: 'orden', error: 'error',
  writing_prompt: 'produccion', speaking_prompt: 'produccion',
};
const ES_AUDIO = (f) => f === 'listen_choice' || f === 'listen_tone';
const ES_LECTURA = (f) => f === 'reading_choice';

const VACIO = {
  id: null, code: '', skill: 'use_of_english', cefr_level: 'A1',
  format: 'choice', competency_tags: [], payload: {}, answer_key: null,
  difficulty: 0.5, active: true,
};

// ───────────────────────────────────────────────────────────────────────────
//  Traductores forma-del-banco ↔ campos del formulario (uno por familia)
// ───────────────────────────────────────────────────────────────────────────
function plantillaPayload(format) {
  const fam = FAMILIA[format];
  if (fam === 'opcion') {
    const p = { question: '', options: [{ id: 'a', text: '' }, { id: 'b', text: '' }] };
    if (ES_AUDIO(format)) p.audio = '';
    if (ES_LECTURA(format)) p.text = '';
    return { payload: p, answer_key: { option_id: 'a' } };
  }
  if (fam === 'orden') return { payload: { prompt: 'Ordena la frase', chips: [] }, answer_key: { sequence: [] } };
  if (fam === 'error') return { payload: { words: [] }, answer_key: { wrong_index: 0 } };
  return { payload: { prompt: '' }, answer_key: null }; // producción
}

export default function ExamsCmsTab() {
  const [salud, setSalud] = useState(null);
  const [esquema, setEsquema] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const [filtros, setFiltros] = useState({ skill: '', cefr_level: '', format: '', active: '', buscar: '' });
  const [editando, setEditando] = useState(null);   // el reactivo en el editor
  const [genOpen, setGenOpen] = useState(false);
  const [vista, setVista] = useState('banco');       // 'banco' | 'examenes'

  // ── Carga inicial: salud + esquema ────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/salud`, { headers: cab() }).then((r) => r.json()).then(setSalud).catch(() => {});
    fetch(`${API}/esquema`, { headers: cab() }).then((r) => r.json()).then(setEsquema).catch(() => {});
  }, []);

  const recargaSalud = useCallback(() => {
    fetch(`${API}/salud`, { headers: cab() }).then((r) => r.json()).then(setSalud).catch(() => {});
  }, []);

  const cargaItems = useCallback(async () => {
    setCargando(true);
    const p = new URLSearchParams();
    if (filtros.skill) p.set('skill', filtros.skill);
    if (filtros.cefr_level) p.set('cefr_level', filtros.cefr_level);
    if (filtros.format) p.set('format', filtros.format);
    if (filtros.active !== '') p.set('active', filtros.active);
    if (filtros.buscar) p.set('buscar', filtros.buscar);
    p.set('limite', '200');
    try {
      const r = await fetch(`${API}/items?${p}`, { headers: cab() });
      const d = await r.json();
      setItems(d.items || []);
      setTotal(d.total || 0);
    } catch { setAviso({ tipo: 'error', txt: 'No se pudo cargar el banco.' }); }
    setCargando(false);
  }, [filtros]);

  useEffect(() => { cargaItems(); }, [cargaItems]);

  const CEFR = esquema?.cefr || ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
  const SKILLS = esquema?.skills || [];
  const FORMATOS = esquema?.formatos || {};

  // ── Guardar (crear o editar) ──────────────────────────────────────────────
  async function guarda() {
    if (!editando) return;
    const esNuevo = !editando.id;
    const cuerpo = {
      code: editando.code.trim(),
      skill: editando.skill,
      cefr_level: editando.cefr_level,
      format: editando.format,
      competency_tags: editando.competency_tags,
      payload: editando.payload,
      answer_key: editando.answer_key,
      difficulty: Number(editando.difficulty),
      active: editando.active,
    };
    try {
      const r = await fetch(
        esNuevo ? `${API}/items` : `${API}/items/${editando.id}`,
        { method: esNuevo ? 'POST' : 'PUT', headers: cab(), body: JSON.stringify(cuerpo) },
      );
      const d = await r.json();
      if (!r.ok) { setAviso({ tipo: 'error', txt: loQueDijo(d) }); return; }
      setAviso({ tipo: 'ok', txt: esNuevo ? 'Reactivo creado.' : 'Reactivo actualizado.' });
      setEditando(null);
      cargaItems();
      recargaSalud();
    } catch { setAviso({ tipo: 'error', txt: 'Fallo de conexión al guardar.' }); }
  }

  async function desactiva(item) {
    if (!window.confirm(`¿Desactivar ${item.code}? Queda oculto pero conserva su historia.`)) return;
    try {
      const r = await fetch(`${API}/items/${item.id}`, { method: 'DELETE', headers: cab() });
      const d = await r.json();
      if (!r.ok) { setAviso({ tipo: 'error', txt: loQueDijo(d) }); return; }
      setAviso({ tipo: 'ok', txt: `${item.code} desactivado.` });
      cargaItems(); recargaSalud();
    } catch { setAviso({ tipo: 'error', txt: 'Fallo de conexión.' }); }
  }

  function nuevo() {
    const { payload, answer_key } = plantillaPayload('choice');
    setEditando({ ...VACIO, payload, answer_key });
  }

  function editar(item) {
    setEditando(JSON.parse(JSON.stringify(item)));   // copia, no referencia
  }

  return (
    <div className="ecms">
      <header className="ecms-head">
        <div>
          <h2>Examen de Nivel · Banco de Reactivos</h2>
          <p className="ecms-sub">
            Los ocho tipos de juego del examen: opción, ordenar, cazar el error,
            audio, entonación, hablar y escribir. Créalos, edítalos y aliméntalos con IA.
          </p>
        </div>
        {vista === 'banco' && (
          <div className="ecms-acciones">
            <button className="ecms-btn ecms-btn-ia" onClick={() => setGenOpen(true)}>✨ Generar con IA</button>
            <button className="ecms-btn ecms-btn-primario" onClick={nuevo}>+ Nuevo reactivo</button>
          </div>
        )}
      </header>

      <div className="ecms-vista-switch">
        <button className={`ecms-vista-btn ${vista === 'banco' ? 'ecms-vista-on' : ''}`}
                onClick={() => setVista('banco')}>Banco de reactivos</button>
        <button className={`ecms-vista-btn ${vista === 'examenes' ? 'ecms-vista-on' : ''}`}
                onClick={() => setVista('examenes')}>Exámenes</button>
        <button className={`ecms-vista-btn ${vista === 'observabilidad' ? 'ecms-vista-on' : ''}`}
                onClick={() => setVista('observabilidad')}>Observabilidad</button>
      </div>

      {aviso && (
        <div className={`ecms-aviso ecms-aviso-${aviso.tipo}`} onClick={() => setAviso(null)}>
          {aviso.txt} <span className="ecms-x">✕</span>
        </div>
      )}

      {vista === 'observabilidad' ? (
        <Observabilidad onAviso={setAviso} />
      ) : vista === 'examenes' ? (
        <ExamsManager salud={salud} esquema={esquema} onAviso={setAviso} />
      ) : (
        <>
          {salud && <MatrizSalud salud={salud} CEFR={CEFR} SKILLS={SKILLS}
                                 onCelda={(s, c) => setFiltros((f) => ({ ...f, skill: s, cefr_level: c }))} />}

          <Filtros filtros={filtros} setFiltros={setFiltros} CEFR={CEFR} SKILLS={SKILLS} FORMATOS={FORMATOS} total={total} />

          <ListaItems items={items} cargando={cargando} FORMATOS={FORMATOS}
                      onEditar={editar} onDesactivar={desactiva} />

          {editando && (
            <Editor editando={editando} setEditando={setEditando} esquema={esquema}
                    CEFR={CEFR} SKILLS={SKILLS} FORMATOS={FORMATOS}
                    onGuardar={guarda} onCerrar={() => setEditando(null)} />
          )}

          {genOpen && (
            <Generador esquema={esquema} CEFR={CEFR} SKILLS={SKILLS} FORMATOS={FORMATOS}
                       onCerrar={() => setGenOpen(false)}
                       onUsar={(borrador) => { setGenOpen(false); setEditando({ ...VACIO, ...borrador, code: '' }); }} />
          )}
        </>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//  Matriz de salud: skill × nivel, los huecos en rojo
// ───────────────────────────────────────────────────────────────────────────
function MatrizSalud({ salud, CEFR, SKILLS, onCelda }) {
  const skillList = SKILLS.length ? SKILLS : Object.keys(salud.matriz).map((id) => ({ id, label: id }));
  return (
    <div className="ecms-salud">
      <div className="ecms-salud-cifras">
        <span><b>{salud.total}</b> reactivos</span>
        <span><b>{salud.activos}</b> activos</span>
        <span className={salud.n_huecos ? 'ecms-hueco-cnt' : ''}><b>{salud.n_huecos}</b> huecos</span>
        <span><b>{salud.intentos}</b> intentos · <b>{salud.respuestas}</b> respuestas</span>
      </div>
      <div className="ecms-matriz-wrap">
        <table className="ecms-matriz">
          <thead>
            <tr><th>Destreza</th>{CEFR.map((c) => <th key={c}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {skillList.map((s) => (
              <tr key={s.id}>
                <td className="ecms-matriz-skill">{s.label}</td>
                {CEFR.map((c) => {
                  const n = salud.matriz[s.id]?.[c] ?? 0;
                  return (
                    <td key={c} className={`ecms-celda ${n === 0 ? 'ecms-celda-hueco' : ''}`}
                        onClick={() => onCelda(s.id, c)} title={`${s.label} ${c}: ${n}`}>
                      {n === 0 ? '·' : n}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//  Filtros
// ───────────────────────────────────────────────────────────────────────────
function Filtros({ filtros, setFiltros, CEFR, SKILLS, FORMATOS, total }) {
  const set = (k, v) => setFiltros((f) => ({ ...f, [k]: v }));
  return (
    <div className="ecms-filtros">
      <input className="ecms-inp" placeholder="Buscar por código…" value={filtros.buscar}
             onChange={(e) => set('buscar', e.target.value)} />
      <select className="ecms-inp" value={filtros.skill} onChange={(e) => set('skill', e.target.value)}>
        <option value="">Toda destreza</option>
        {SKILLS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
      </select>
      <select className="ecms-inp" value={filtros.cefr_level} onChange={(e) => set('cefr_level', e.target.value)}>
        <option value="">Todo nivel</option>
        {CEFR.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <select className="ecms-inp" value={filtros.format} onChange={(e) => set('format', e.target.value)}>
        <option value="">Todo formato</option>
        {Object.entries(FORMATOS).map(([id, f]) => <option key={id} value={id}>{f.label}</option>)}
      </select>
      <select className="ecms-inp" value={filtros.active} onChange={(e) => set('active', e.target.value)}>
        <option value="">Activos e inactivos</option>
        <option value="true">Solo activos</option>
        <option value="false">Solo inactivos</option>
      </select>
      <span className="ecms-total">{total} resultados</span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//  Lista
// ───────────────────────────────────────────────────────────────────────────
function ListaItems({ items, cargando, FORMATOS, onEditar, onDesactivar }) {
  if (cargando) return <div className="ecms-vacio">Cargando…</div>;
  if (!items.length) return <div className="ecms-vacio">No hay reactivos con esos filtros.</div>;
  return (
    <div className="ecms-tabla-wrap">
      <table className="ecms-tabla">
        <thead>
          <tr><th>Código</th><th>Destreza</th><th>Nivel</th><th>Formato</th><th>Enunciado</th><th>Dif.</th><th></th></tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className={it.active ? '' : 'ecms-fila-off'}>
              <td className="ecms-code">{it.code}</td>
              <td>{it.skill_label}</td>
              <td><span className="ecms-nivel">{it.cefr_level}</span></td>
              <td>{FORMATOS[it.format]?.label || it.format}</td>
              <td className="ecms-enun">{resumen(it)}</td>
              <td>{it.difficulty?.toFixed(2)}</td>
              <td className="ecms-fila-acc">
                <button className="ecms-mini" onClick={() => onEditar(it)}>Editar</button>
                {it.active && <button className="ecms-mini ecms-mini-off" onClick={() => onDesactivar(it)}>Desactivar</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function resumen(it) {
  const p = it.payload || {};
  return (p.question || p.prompt || p.text || (p.words || []).join(' ') || (p.chips || []).join(' ') || '—').slice(0, 70);
}

// ───────────────────────────────────────────────────────────────────────────
//  Editor con vista previa
// ───────────────────────────────────────────────────────────────────────────
function Editor({ editando, setEditando, CEFR, SKILLS, FORMATOS, onGuardar, onCerrar }) {
  const set = (campo, valor) => setEditando((e) => ({ ...e, [campo]: valor }));
  const setPay = (fn) => setEditando((e) => ({ ...e, payload: fn(e.payload || {}) }));
  const setKey = (fn) => setEditando((e) => ({ ...e, answer_key: fn(e.answer_key || {}) }));

  function cambiaFormato(nf) {
    const { payload, answer_key } = plantillaPayload(nf);
    setEditando((e) => ({ ...e, format: nf, payload, answer_key }));
  }

  const fam = FAMILIA[editando.format];
  const itemPreview = { item_id: 'preview', format: editando.format, payload: editando.payload || {} };

  return (
    <div className="ecms-modal" onClick={onCerrar}>
      <div className="ecms-editor" onClick={(e) => e.stopPropagation()}>
        <div className="ecms-editor-cols">
          {/* ── FORMULARIO ─────────────────────────────────────────────── */}
          <div className="ecms-form">
            <h3>{editando.id ? `Editar ${editando.code}` : 'Nuevo reactivo'}</h3>

            <label className="ecms-lbl">Código único
              <input className="ecms-inp" value={editando.code} maxLength={24}
                     placeholder="UOE-B1-014" onChange={(e) => set('code', e.target.value)} />
            </label>

            <div className="ecms-row3">
              <label className="ecms-lbl">Destreza
                <select className="ecms-inp" value={editando.skill} onChange={(e) => set('skill', e.target.value)}>
                  {SKILLS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </label>
              <label className="ecms-lbl">Nivel
                <select className="ecms-inp" value={editando.cefr_level} onChange={(e) => set('cefr_level', e.target.value)}>
                  {CEFR.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="ecms-lbl">Formato
                <select className="ecms-inp" value={editando.format} onChange={(e) => cambiaFormato(e.target.value)}>
                  {Object.entries(FORMATOS).map(([id, f]) => <option key={id} value={id}>{f.label}</option>)}
                </select>
              </label>
            </div>

            {fam === 'opcion' && <CamposOpcion editando={editando} setPay={setPay} setKey={setKey} />}
            {fam === 'orden' && <CamposOrden editando={editando} setPay={setPay} setKey={setKey} />}
            {fam === 'error' && <CamposError editando={editando} setPay={setPay} setKey={setKey} />}
            {fam === 'produccion' && <CamposProduccion editando={editando} setPay={setPay} />}

            <label className="ecms-lbl">Competencias (etiquetas, separadas por coma)
              <input className="ecms-inp" value={(editando.competency_tags || []).join(', ')}
                     onChange={(e) => set('competency_tags', e.target.value.split(',').map((t) => t.trim()).filter(Boolean))} />
            </label>

            <div className="ecms-row3">
              <label className="ecms-lbl">Dificultad (0–1)
                <input className="ecms-inp" type="number" min="0" max="1" step="0.05"
                       value={editando.difficulty} onChange={(e) => set('difficulty', e.target.value)} />
              </label>
              <label className="ecms-lbl ecms-lbl-check">
                <input type="checkbox" checked={editando.active} onChange={(e) => set('active', e.target.checked)} />
                Activo (visible para el examen)
              </label>
            </div>

            <div className="ecms-editor-botones">
              <button className="ecms-btn" onClick={onCerrar}>Cancelar</button>
              <button className="ecms-btn ecms-btn-primario" onClick={onGuardar}>Guardar</button>
            </div>
          </div>

          {/* ── VISTA PREVIA (el renderer del alumno) ──────────────────── */}
          <div className="ecms-preview">
            <div className="ecms-preview-tit">Vista previa · lo que verá el alumno</div>
            <div className="ecms-preview-caja as-scope">
              <ItemRenderer item={itemPreview} onSubmit={() => {}} disabled />
            </div>
            {fam === 'opcion' && (
              <p className="ecms-preview-clave">Respuesta correcta: <b>{editando.answer_key?.option_id}</b></p>
            )}
            {fam === 'error' && (
              <p className="ecms-preview-clave">Palabra mala: posición <b>{editando.answer_key?.wrong_index}</b></p>
            )}
            {fam === 'produccion' && (
              <p className="ecms-preview-clave">Producción libre — la evalúa la IA, sin clave.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Campos: familia OPCIÓN ───────────────────────────────────────────────────
function CamposOpcion({ editando, setPay, setKey }) {
  const p = editando.payload || {};
  const opts = p.options || [];
  const correcta = editando.answer_key?.option_id;
  const setOpt = (i, text) => setPay((pp) => ({ ...pp, options: pp.options.map((o, j) => j === i ? { ...o, text } : o) }));
  const addOpt = () => setPay((pp) => {
    if ((pp.options || []).length >= 4) return pp;
    const id = String.fromCharCode(97 + (pp.options?.length || 0)); // a,b,c,d
    return { ...pp, options: [...(pp.options || []), { id, text: '' }] };
  });
  const delOpt = (i) => setPay((pp) => ({ ...pp, options: pp.options.filter((_, j) => j !== i) }));
  return (
    <>
      {ES_AUDIO(editando.format) && (
        <label className="ecms-lbl">Texto que se lee en voz alta (audio)
          <input className="ecms-inp" value={p.audio || ''} onChange={(e) => setPay((pp) => ({ ...pp, audio: e.target.value }))} />
        </label>
      )}
      {ES_LECTURA(editando.format) && (
        <label className="ecms-lbl">Pasaje de lectura
          <textarea className="ecms-inp ecms-area" value={p.text || ''} onChange={(e) => setPay((pp) => ({ ...pp, text: e.target.value }))} />
        </label>
      )}
      <label className="ecms-lbl">Pregunta / enunciado
        <input className="ecms-inp" value={p.question || ''} onChange={(e) => setPay((pp) => ({ ...pp, question: e.target.value }))} />
      </label>
      <div className="ecms-opts">
        <div className="ecms-opts-tit">Opciones · marca la correcta</div>
        {opts.map((o, i) => (
          <div key={o.id} className="ecms-opt">
            <input type="radio" name="correcta" checked={correcta === o.id}
                   onChange={() => setKey(() => ({ option_id: o.id }))} />
            <span className="ecms-opt-id">{o.id}</span>
            <input className="ecms-inp" value={o.text} onChange={(e) => setOpt(i, e.target.value)} />
            {opts.length > 2 && <button className="ecms-mini ecms-mini-off" onClick={() => delOpt(i)}>×</button>}
          </div>
        ))}
        {opts.length < 4 && <button className="ecms-mini" onClick={addOpt}>+ opción</button>}
      </div>
    </>
  );
}

// ── Campos: familia ORDEN ────────────────────────────────────────────────────
function CamposOrden({ editando, setPay, setKey }) {
  const p = editando.payload || {};
  const frase = (editando.answer_key?.sequence || p.chips || []).join(' ');
  function onFrase(txt) {
    const words = txt.split(/\s+/).filter(Boolean);
    setPay((pp) => ({ ...pp, prompt: pp.prompt || 'Ordena la frase', chips: words }));
    setKey(() => ({ sequence: words }));
  }
  return (
    <>
      <label className="ecms-lbl">Instrucción
        <input className="ecms-inp" value={p.prompt || ''} onChange={(e) => setPay((pp) => ({ ...pp, prompt: e.target.value }))} />
      </label>
      <label className="ecms-lbl">La frase en su orden correcto (el examen la baraja sola)
        <input className="ecms-inp" value={frase} placeholder="I have lived here for years"
               onChange={(e) => onFrase(e.target.value)} />
      </label>
    </>
  );
}

// ── Campos: familia ERROR ────────────────────────────────────────────────────
function CamposError({ editando, setPay, setKey }) {
  const p = editando.payload || {};
  const words = p.words || [];
  const mala = editando.answer_key?.wrong_index;
  function onFrase(txt) {
    const w = txt.split(/\s+/).filter(Boolean);
    setPay((pp) => ({ ...pp, words: w }));
    if (mala == null || mala >= w.length) setKey(() => ({ wrong_index: 0 }));
  }
  return (
    <>
      <label className="ecms-lbl">La frase (una palabra debe estar mal)
        <input className="ecms-inp" value={words.join(' ')} placeholder="He go to school every day"
               onChange={(e) => onFrase(e.target.value)} />
      </label>
      {words.length > 0 && (
        <div className="ecms-error-words">
          <div className="ecms-opts-tit">Toca la palabra equivocada</div>
          <div className="ecms-error-fila">
            {words.map((w, i) => (
              <button key={i} className={`ecms-word ${mala === i ? 'ecms-word-mala' : ''}`}
                      onClick={() => setKey(() => ({ wrong_index: i }))}>{w}</button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ── Campos: familia PRODUCCIÓN ───────────────────────────────────────────────
function CamposProduccion({ editando, setPay }) {
  const p = editando.payload || {};
  return (
    <>
      <label className="ecms-lbl">Consigna
        <textarea className="ecms-inp ecms-area" value={p.prompt || ''}
                  onChange={(e) => setPay((pp) => ({ ...pp, prompt: e.target.value }))} />
      </label>
      {editando.format === 'writing_prompt' && (
        <label className="ecms-lbl">Mínimo de palabras (opcional)
          <input className="ecms-inp" type="number" min="0" value={p.min_words || ''}
                 onChange={(e) => setPay((pp) => ({ ...pp, min_words: e.target.value ? Number(e.target.value) : undefined }))} />
        </label>
      )}
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//  Generador con IA — propone borradores, no los guarda
// ───────────────────────────────────────────────────────────────────────────
function Generador({ CEFR, SKILLS, FORMATOS, onCerrar, onUsar }) {
  const [cfg, setCfg] = useState({ skill: 'use_of_english', cefr_level: 'A1', format: 'choice', cantidad: 3, tema: '' });
  const [cargando, setCargando] = useState(false);
  const [borradores, setBorradores] = useState([]);
  const [descartes, setDescartes] = useState([]);
  const [error, setError] = useState(null);

  async function genera() {
    setCargando(true); setError(null); setBorradores([]); setDescartes([]);
    try {
      const r = await fetch(`${API}/items/generate`, {
        method: 'POST', headers: cab(), body: JSON.stringify({ ...cfg, cantidad: Number(cfg.cantidad) }),
      });
      const d = await r.json();
      if (!r.ok) { setError(loQueDijo(d)); }
      else { setBorradores(d.borradores || []); setDescartes(d.descartes || []); }
    } catch { setError('Fallo de conexión con la IA.'); }
    setCargando(false);
  }

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  return (
    <div className="ecms-modal" onClick={onCerrar}>
      <div className="ecms-gen" onClick={(e) => e.stopPropagation()}>
        <h3>✨ Generar reactivos con IA</h3>
        <p className="ecms-sub">Son propuestas: revísalas y créalas tú. Cada tanda gasta una llamada.</p>
        <div className="ecms-gen-cfg">
          <select className="ecms-inp" value={cfg.skill} onChange={(e) => set('skill', e.target.value)}>
            {SKILLS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <select className="ecms-inp" value={cfg.cefr_level} onChange={(e) => set('cefr_level', e.target.value)}>
            {CEFR.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="ecms-inp" value={cfg.format} onChange={(e) => set('format', e.target.value)}>
            {Object.entries(FORMATOS).map(([id, f]) => <option key={id} value={id}>{f.label}</option>)}
          </select>
          <input className="ecms-inp" type="number" min="1" max="5" value={cfg.cantidad}
                 onChange={(e) => set('cantidad', e.target.value)} />
        </div>
        <input className="ecms-inp" placeholder="Tema (opcional): rutinas, viajes…" value={cfg.tema}
               onChange={(e) => set('tema', e.target.value)} />
        <div className="ecms-gen-acc">
          <button className="ecms-btn" onClick={onCerrar}>Cerrar</button>
          <button className="ecms-btn ecms-btn-ia" onClick={genera} disabled={cargando}>
            {cargando ? 'Generando…' : 'Generar'}
          </button>
        </div>

        {error && <div className="ecms-aviso ecms-aviso-error">{error}</div>}
        {borradores.map((b, i) => (
          <div key={i} className="ecms-borr">
            <div className="ecms-borr-txt">{resumen({ payload: b.payload })}</div>
            <button className="ecms-mini" onClick={() => onUsar(b)}>Usar y editar →</button>
          </div>
        ))}
        {descartes.length > 0 && (
          <p className="ecms-descartes">{descartes.length} propuesta(s) descartada(s) por no cumplir el formato.</p>
        )}
      </div>
    </div>
  );
}

// Traduce el error del backend a algo accionable (nunca «problema de conexión»).
function loQueDijo(d) {
  if (!d) return 'Ocurrió un error.';
  if (typeof d.detail === 'string') return d.detail;
  if (Array.isArray(d.detail)) return d.detail.map((e) => e.msg || '').join(' · ') || 'Datos inválidos.';
  return d.message || 'Ocurrió un error.';
}
